import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  Badge,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Ltr,
  Pager,
  StatusBadge,
  Time,
} from '../components/ui';
import { api } from '../lib/api';
import { fmtNum } from '../lib/format';
import { useSearchText, useUrlState } from '../lib/url';

/** What each persisted candidate status means (shown as help text; values are V3's own). */
const STATUS_HELP: Record<string, string> = {
  pending: 'Imported; not yet evaluated',
  invalid: 'Unusable input (e.g. no valid domain)',
  duplicate: 'Same identity as another candidate row',
  published: 'Linked to an entity with a published revision',
  awaiting_review: 'Linked, but a review blocks publication',
  needs_fetch: 'No provider data yet — needs a Brandfetch fetch',
  ready_offline: 'Provider data available offline (archive); not yet processed',
};

export function CandidatesPage() {
  const { sp, set } = useUrlState();
  const [text, setText] = useSearchText();
  const limit = Number(sp.get('limit') ?? 25);
  const offset = Number(sp.get('offset') ?? 0);
  const overview = useQuery({ queryKey: ['overview'], queryFn: api.overview, staleTime: 30_000 });
  const params = { q: sp.get('q'), status: sp.get('status'), limit, offset };
  const list = useQuery({
    queryKey: ['candidates', params],
    queryFn: () => api.candidates(params),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Candidate records</h1>
          <p>
            Curated input rows (brands.csv, legacy inventory). A candidate is not a brand until
            identity verification links it to a canonical entity.
          </p>
        </div>
      </div>
      {overview.data && (
        <section className="stats" aria-label="Candidates by status">
          {overview.data.facets.candidateStatuses.map((s) => (
            <button
              type="button"
              key={s.value}
              className="stat"
              style={{
                textAlign: 'start',
                cursor: 'pointer',
                outline: sp.get('status') === s.value ? '2px solid var(--accent)' : undefined,
              }}
              onClick={() => set({ status: sp.get('status') === s.value ? null : s.value })}
              aria-pressed={sp.get('status') === s.value}
            >
              <div className="label">
                <StatusBadge kind="candidate" value={s.value ?? 'unknown'} />
              </div>
              <div className="value">{fmtNum(s.count)}</div>
              <div className="sub">{STATUS_HELP[s.value ?? ''] ?? ''}</div>
            </button>
          ))}
        </section>
      )}
      <search className="filters">
        <div className="search">
          <Icon name="search" />
          <input
            className="input"
            type="search"
            placeholder="Search candidate name, localized name or domain"
            aria-label="Search candidates"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
        {sp.get('status') && (
          <span className="chip">
            status: {sp.get('status')}
            <button
              type="button"
              aria-label="Clear status filter"
              onClick={() => set({ status: null })}
            >
              ×
            </button>
          </span>
        )}
      </search>
      <section className="card">
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => list.refetch()} />
        ) : !list.data ? (
          <Loading rows={10} />
        ) : list.data.total === 0 ? (
          <Empty title="No candidate rows match" />
        ) : (
          <>
            <div className="table-wrap">
              <table className="t">
                <thead>
                  <tr>
                    <th scope="col">Candidate</th>
                    <th scope="col">Domain</th>
                    <th scope="col">Category · Region</th>
                    <th scope="col">Status</th>
                    <th scope="col">Canonical entity</th>
                    <th scope="col" className="hide-sm">
                      Provenance
                    </th>
                    <th scope="col" className="hide-sm">
                      Last attempt
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {list.data.items.map((c) => (
                    <tr key={c.id} data-testid="candidate-row">
                      <td>
                        <div style={{ fontWeight: 600 }}>{c.name}</div>
                        {c.nameHe && (
                          <div className="muted" dir="auto" style={{ fontSize: 12 }}>
                            {c.nameHe}
                          </div>
                        )}
                      </td>
                      <td>
                        {c.domain ? <Ltr>{c.domain}</Ltr> : <span className="faint">—</span>}
                        {c.domainOriginal && c.domainOriginal !== c.domain && (
                          <div className="faint" style={{ fontSize: 12 }}>
                            original: <Ltr>{c.domainOriginal}</Ltr>
                          </div>
                        )}
                      </td>
                      <td>
                        <div>{c.category ?? <span className="faint">—</span>}</div>
                        <div className="muted" style={{ fontSize: 12 }}>
                          {c.region ?? '—'}
                        </div>
                      </td>
                      <td>
                        <StatusBadge kind="candidate" value={c.status} />
                        {c.reviewRequired && (
                          <>
                            {' '}
                            <Badge tone="warn">review</Badge>
                          </>
                        )}
                      </td>
                      <td>
                        {c.entity ? (
                          <Link className="link" to={`/brands/${c.entity.slug}`}>
                            {c.entity.name}
                          </Link>
                        ) : (
                          <span className="faint">not resolved</span>
                        )}
                      </td>
                      <td className="hide-sm">
                        <Ltr mono>
                          {c.sourceFile}#{c.sourceRow}
                        </Ltr>
                      </td>
                      <td className="hide-sm">
                        <Time iso={c.lastAttemptAt} relative />
                        {c.lastError && (
                          <div className="err" style={{ fontSize: 12 }}>
                            {c.lastError}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager
              total={list.data.total}
              limit={limit}
              offset={offset}
              onChange={(o) => set({ offset: String(o) }, false)}
              onLimit={(l) => set({ limit: String(l) })}
            />
          </>
        )}
      </section>
    </>
  );
}
